import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/authSlice";
import {
  useCreateChatConversationMutation,
  useGetChatConversationsQuery,
} from "../../../../app/communicationApiSlice";
import { useGetClassesQuery } from "../../classroom/classApiSlice";
import { useGetParentStudentsQuery } from "../../../../app/parentApiSlice";
import { useGetClassroomStudentsPaginatedQuery } from "../../classroom/classApiSlice";
import toast from "../../../../helpers/toast";

export default function NewConversationDialog({ open, onClose, onCreated }) {
  const user = useSelector(selectCurrentUser);
  const roles = user?.roles?.map((r) => r?.role?.name).filter(Boolean) || [];
  const isParent = roles.includes("parent");
  const isTeacher = roles.includes("teacher");
  const isAdmin = roles.includes("admin");
  const isClassManager = isTeacher || isAdmin;

  const { data: classesData } = useGetClassesQuery("all/mine", {
    skip: !isClassManager,
  });
  const classes = classesData || [];

  const { data: parentStudents = [] } = useGetParentStudentsQuery(undefined, {
    skip: !isParent,
  });

  const [type, setType] = useState(
    isParent ? "parent_admin" : "class_manager_parent"
  );
  const [classroomId, setClassroomId] = useState(classes[0]?.id || "");
  const [studentId, setStudentId] = useState("");
  const [body, setBody] = useState("");
  const [createConversation, { isLoading }] = useCreateChatConversationMutation();

  const selectedClassId = classroomId || classes[0]?.id || "";

  const { data: classStudentsData } = useGetClassroomStudentsPaginatedQuery(
    { classroom_id: selectedClassId, page: 1, per_page: 100 },
    { skip: !isClassManager || !selectedClassId }
  );
  const classStudents = classStudentsData?.students || [];

  const recipientStudents = isParent && !isClassManager ? parentStudents : classStudents;

  const needsStudent = type !== "parent_admin";
  const needsClass = isClassManager && type === "class_manager_parent";

  const canSubmit =
    body.trim() &&
    (!needsStudent || studentId) &&
    (!needsClass || classroomId);

  const handleSubmit = async () => {
    try {
      const conv = await createConversation({
        type,
        student_id: needsStudent ? studentId : undefined,
        classroom_id: needsClass ? classroomId : undefined,
        body: body.trim(),
      }).unwrap();
      setBody("");
      setStudentId("");
      toast.success("Conversation started");
      onCreated?.(conv.id);
      onClose();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Could not start conversation");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={800}>New message</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          {/* Recipient type */}
          <FormControl fullWidth size="small">
            <InputLabel>To</InputLabel>
            <Select label="To" value={type} onChange={(e) => setType(e.target.value)}>
              {isParent && <MenuItem value="parent_admin">School admin</MenuItem>}
              {isParent && <MenuItem value="parent_class_manager">Class manager</MenuItem>}
              {isClassManager && (
                <MenuItem value="class_manager_parent">Parent (via student)</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Class selector (class manager → parent) */}
          {needsClass && (
            <FormControl fullWidth size="small">
              <InputLabel>Class</InputLabel>
              <Select
                label="Class"
                value={classroomId}
                onChange={(e) => {
                  setClassroomId(e.target.value);
                  setStudentId("");
                }}
              >
                {classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Student selector */}
          {needsStudent && (
            <FormControl fullWidth size="small">
              <InputLabel>Student</InputLabel>
              <Select
                label="Student"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
              >
                {recipientStudents.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.first_name} {s.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Message body */}
          <TextField
            multiline
            minRows={3}
            fullWidth
            placeholder="Write your message…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isLoading || !canSubmit}
          onClick={handleSubmit}
          startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ textTransform: "none" }}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
